import React, { useState } from "react";

import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { Button, Box, Textarea } from "@mantine/core";
import { ethers } from "ethers";
import { isAddress } from "ethers/lib/utils";

import { Condition, ConditionType } from "apollo/graphql.generated";
import { ALCHEMY_API_KEY_ENS } from "config";

export type SetAllowlistProps = {
  onSave: (data: Condition) => void;
  submitText?: string;
  initialCondition?: Condition;
};

const SetAllowlist = ({
  onSave,
  submitText = "Add condition",
  initialCondition,
}: SetAllowlistProps) => {
  const [allowlist, setAllowlist] = useState<string[]>([]);
  const [allowlistRaw, setAllowlistRaw] = useState<string>("");
  const [validationError, setValidationError] = useState<boolean>(false);
  const [validatingAllowlist, setValidatingAllowlist] =
    useState<boolean>(false);
  const [lengthError, setLengthError] = useState<boolean>(false);
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const [ensCache, setEnsCache] = useState<any>({});

  const ensUrl = `https://eth-mainnet.alchemyapi.io/v2/${ALCHEMY_API_KEY_ENS}`;
  const provider = ALCHEMY_API_KEY_ENS
    ? new ethers.providers.JsonRpcProvider(ensUrl)
    : ethers.getDefaultProvider();

  const onSetAllowlist = async (allowlistString: string) => {
    setValidatingAllowlist(true);

    if (allowlistString === "") {
      setValidationError(false);
      setLengthError(false);
      setValidatingAllowlist(false);
      setAllowlist([]);
      setAllowlistRaw("");
      return;
    }

    const allowlistParsed = allowlistString.split(",").map((s) => s.trim());
    if (allowlistParsed.at(-1) === "") {
      allowlistParsed.pop(); // handle case where csv ends with comma/whitespace
    }
    if (allowlistParsed.length > 20000) {
      setLengthError(true);
      return;
    }

    const allowlistNoDuplicates = [...new Set(allowlistParsed)];

    const ensNames: string[] = [];
    for (let i = 0; i < allowlistNoDuplicates.length; i++) {
      if (allowlistNoDuplicates[i].endsWith(".eth")) {
        ensNames.push(allowlistNoDuplicates[i]);
      } else if (!isAddress(allowlistNoDuplicates[i])) {
        setValidationError(true);
        return;
      }
    }

    const ensResolved = await Promise.all(
      ensNames.map(async (n) => {
        if (ensCache[n]) {
          return ensCache[n];
        }
        const address = await provider.resolveName(n);
        const newCache = { ...ensCache };
        newCache[n] = address;
        setEnsCache(newCache);
        return address;
      })
    );

    for (let i = 0; i < ensResolved.length; i++) {
      if (!isAddress(ensResolved[i] ?? "")) {
        setValidationError(true);
        return;
      }
    }

    const finalAllowlist = allowlistNoDuplicates
      .filter((s) => !s.endsWith(".eth"))
      .concat(ensResolved as string[]);

    setValidationError(false);
    setLengthError(false);
    setValidatingAllowlist(false);
    setAllowlist(finalAllowlist);
    setAllowlistRaw(allowlistParsed.join(", ")); // use better looking list, in case initial input is messy
  };

  const addOrUpdateCondition = () => {
    onSave({
      amount: 1,
      data: { allowlist, allowlistRaw, allowlistLength: allowlist.length },
      type: ConditionType.ALLOWLIST,
    });
  };

  return (
    <>
      <Box>
        <Textarea
          label="Specify comma-separated list of wallet addresses"
          placeholder="Wallet addresses"
          onChange={(e) => onSetAllowlist(e.currentTarget.value)}
          defaultValue={initialCondition?.data?.allowlist?.join(",\n") ?? ""}
          minRows={10}
          maxRows={10}
          autosize
          spellCheck={false}
          error={
            validationError
              ? "Provide a comma-separated list of valid wallet addresses or ENS domains"
              : lengthError &&
                "The allowlist can contain a maximum of 20000 valid addresses or ENS domains"
          }
        />
        <ModalSection>
          <Button
            size="xl"
            fullWidth
            onClick={addOrUpdateCondition}
            disabled={
              allowlist.length === 0 ||
              validatingAllowlist ||
              validationError ||
              lengthError
            }
          >
            {submitText}
          </Button>
        </ModalSection>
      </Box>
    </>
  );
};

export default SetAllowlist;
